import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/domains/identity/AuthProvider';
import { Button, Dialog, Input, useErrorMessage } from '@/shared/ui';

/**
 * Re-authentication in place.
 *
 * When a session dies mid-edit, throwing the user at /login discards everything they were
 * doing. This dialog signs them back in on top of the current page; the API client then
 * replays the request that triggered it.
 */
export function ReauthDialog() {
  const { t } = useTranslation();
  const { reauthOpen, resolveReauth, login, user } = useAuth();
  const toMessage = useErrorMessage();

  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(email, password);
      setPassword('');
      resolveReauth(true);
    } catch (caught) {
      setError(toMessage(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={reauthOpen}
      // Not dismissible by Escape/outside click: the caller is awaiting an answer.
      dismissible={false}
      onOpenChange={() => {}}
      title={t('auth.sessionExpiredTitle')}
      description={t('auth.sessionExpiredBody')}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          type="email"
          autoComplete="username"
          label={t('auth.email')}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <Input
          type="password"
          autoComplete="current-password"
          label={t('auth.password')}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoFocus
          required
        />
        {error && (
          <p className="rounded border border-danger/30 bg-danger-subtle px-3 py-2 text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => resolveReauth(false)} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            {t('auth.reauthenticate')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
